// What the model is doing with a tool RIGHT NOW, in words a user reads.
//
// While a model writes a tool call's arguments — a whole file for `write`, a
// script for `bash`, a deck.js for a presentation — the sidecar forwards the
// raw argument text on the pending part's `streamedInput` field (see
// session/processor.ts, STREAMED_INPUT_FIELD). Before that existed the window
// saw nothing for minutes and one user sat through a shimmering "Terminal"
// label that could not tell work from a hang. From that text, while it is
// still incomplete JSON, this reads:
//
//   * the TARGET — the file name, the command's own description, the
//     sub-agent's task, the skill's name — usually within the first chunk;
//   * the SIZE so far, which grows visibly;
//   * for office work, PROGRESS in the task's own terms: slides added to a
//     deck, sheets and rows to a workbook, paragraphs to a document. Counted
//     by marker, for the builders the bundled skills use (pptxgenjs, ExcelJS,
//     docx) and their Python counterparts a model falls back to.
//
// Pure, so it can be tested and reused by the turn's status line and the plan
// dock alike.

type AnyToolPart = {
  type: "tool"
  tool: string
  state: { status: string; input?: Record<string, unknown> }
  streamedInput?: string
}

export type ToolActivitySemantic =
  | { kind: "slides"; count: number }
  | { kind: "sheets"; count: number; rows: number }
  | { kind: "paragraphs"; count: number }

export type ToolActivity = {
  tool: string
  /** preparing: the model is still writing the call; running: the tool executes. */
  state: "preparing" | "running"
  target?: string
  /** Characters of the arguments streamed so far (preparing only). */
  chars: number
  semantic?: ToolActivitySemantic
}

// `command` is deliberately absent: a shell command is not a status. The
// model writes `command` before `description`, so reading it put raw shell —
// «Готовлю: cd /tmp/xlsx9 && python3 -c "» — in the row until the description
// arrived and replaced it, a second line of text jumping mid-step. A command
// with no description yet says nothing beyond its phase.
const TARGET_KEYS = ["description", "filePath", "path", "name", "pattern", "url", "query"] as const

// The plan itself: its arguments are the todo list, and «Шаг 6 / 6 · 2,1 КБ»
// — the size of a list being rewritten — is a number nobody can read.
const SILENT = new Set(["todowrite", "todoupdate"])

// apply_patch has one argument, the patch, so no key names its file: the file
// is in the patch's own header, which leads the text.
// In the streamed (still JSON-escaped) text a line ends at a `\n` escape; a
// decoded Windows path has bare backslashes (`C:\new\a.ts`), so the decoded
// text is read to the real end of line instead.
const PATCH_HEADER_ESCAPED = /\*\*\* (?:Add|Update) File: ((?:[^\\"\n]|\\[^n])+)/
const PATCH_HEADER = /\*\*\* (?:Add|Update) File: ([^\n]+)/
const PATCH_TOOLS = new Set(["apply_patch", "patch"])

/** The first file a patch adds or updates — raw (still JSON-escaped) or decoded text. */
export function patchTarget(text: string, escaped: boolean) {
  const head = (escaped ? PATCH_HEADER_ESCAPED : PATCH_HEADER).exec(text.slice(0, TARGET_SCAN))
  if (!head) return undefined
  const value = (escaped ? decodePartial(head[1]) : head[1])?.trim()
  return value ? basename(value) : undefined
}

// Keys whose value is a path: shown by basename, a full path does not fit.
const PATH_KEYS = new Set(["filePath", "path"])

// A target is one line of a status row; decoding more of a value than this is
// wasted work — a `bash` heredoc or a `write` body can run to megabytes, and
// this is re-read on every streamed chunk.
const TARGET_SCAN = 4096

/** The (possibly unterminated) string value of `key` in a partial JSON object, unescaped. */
export function partialJsonString(raw: string, key: string, limit = Infinity): string | undefined {
  const head = new RegExp(`"${key}"\\s*:\\s*"`).exec(raw)
  if (!head) return undefined
  // Scanned by hand, not by one regex over the value: the value can run to
  // megabytes, and a backtracking match over it gives up (JavaScriptCore
  // returned no match at all on a 2 MB heredoc).
  const from = head.index + head[0].length
  const stop = Math.min(raw.length, from + limit)
  let end = from
  while (end < stop) {
    const char = raw.charCodeAt(end)
    if (char === 0x22) break // closing quote
    end += char === 0x5c ? 2 : 1 // a backslash escapes the next character
  }
  return decodePartial(raw.slice(from, Math.min(end, stop)))
}

/** The still-escaped body of a JSON string cut anywhere, decoded as far as it goes. */
function decodePartial(escaped: string): string | undefined {
  // An escape cut at the end leaves a lone trailing backslash, which would not parse.
  const body = escaped.replace(/\\+$/, (tail) => (tail.length % 2 ? tail.slice(1) : tail))
  try {
    return JSON.parse(`"${body}"`) as string
  } catch {
    // A \u escape cut mid-way: drop from its backslash on.
    const cut = body.lastIndexOf("\\")
    try {
      return JSON.parse(`"${cut >= 0 ? body.slice(0, cut) : body}"`) as string
    } catch {
      return undefined
    }
  }
}

function basename(value: string) {
  const parts = value.split(/[\\/]/).filter(Boolean)
  return parts[parts.length - 1] ?? value
}

function firstLine(value: string, limit = 80) {
  const end = value.indexOf("\n")
  const line = (end < 0 ? value : value.slice(0, end)).trim()
  return line.length > limit ? `${line.slice(0, limit - 1)}…` : line
}

function targetFrom(read: (key: string) => string | undefined) {
  for (const key of TARGET_KEYS) {
    const value = read(key)
    if (typeof value !== "string" || !value.trim()) continue
    return PATH_KEYS.has(key) ? basename(value) : firstLine(value)
  }
  return undefined
}

function count(text: string, pattern: RegExp) {
  return (text.match(pattern) ?? []).length
}

/** Progress in the task's own terms, from the code being written (escaped JSON is fine to count in). */
export function semanticProgress(text: string): ToolActivitySemantic | undefined {
  const slides = count(text, /\.addSlide\(|\.add_slide\(/g)
  if (slides > 0) return { kind: "slides", count: slides }
  const sheets = count(text, /\.addWorksheet\(|\.create_sheet\(/g)
  if (sheets > 0) return { kind: "sheets", count: sheets, rows: count(text, /\.addRows?\(|\bws\.append\(/g) }
  const paragraphs = count(text, /new Paragraph\(|\.add_paragraph\(/g)
  if (paragraphs > 0) return { kind: "paragraphs", count: paragraphs }
  return undefined
}

// Tools whose arguments ARE the code that builds the document: a `write` of
// deck.js, a Python heredoc in `bash`. An `edit` quotes the same markers in its
// old and new text, and a `grep` for addSlide contains one — counting those
// would report slides that do not exist.
const AUTHORING = new Set(["write", "bash"])

export function toolActivity(part: AnyToolPart): ToolActivity | undefined {
  if (SILENT.has(part.tool)) return undefined
  const status = part.state.status
  if (status === "pending") {
    const raw = part.streamedInput ?? ""
    return {
      tool: part.tool,
      state: "preparing",
      target: raw
        ? PATCH_TOOLS.has(part.tool)
          ? patchTarget(raw, true)
          : targetFrom((key) => partialJsonString(raw, key, TARGET_SCAN))
        : undefined,
      chars: raw.length,
      semantic: raw && AUTHORING.has(part.tool) ? semanticProgress(raw) : undefined,
    }
  }
  if (status === "running") {
    const input = part.state.input ?? {}
    return {
      tool: part.tool,
      state: "running",
      target:
        PATCH_TOOLS.has(part.tool) && typeof input.patchText === "string"
          ? patchTarget(input.patchText, false)
          : targetFrom((key) => (typeof input[key] === "string" ? (input[key] as string) : undefined)),
      chars: 0,
    }
  }
  return undefined
}

/** "14,2 КБ" / "14.2 KB" — the size of what the model has written so far. */
export function formatStreamedSize(chars: number, locale: string, unit: { kb: string; b: string }) {
  if (chars < 1024) return `${chars} ${unit.b}`
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(chars / 1024)} ${unit.kb}`
}

type Translate = (key: string, params?: Record<string, string | number>) => string

// Tools whose streamed argument IS a file being written: "пишу deck.js" reads
// right for them, and for a command "готовлю: Собрать XLSX" does.
const FILE_WRITERS = new Set(["write", "edit", "multiedit", "patch", "apply_patch"])

// A running tool in its own words where a generic "выполняю" says less: a skill
// is being loaded, a sub-agent is working on its task.
const RUNNING_KEYS: Record<string, string> = {
  skill: "ui.toolActivity.runningSkill",
  task: "ui.toolActivity.runningTask",
}

/**
 * One line for the status row and the plan dock:
 *   "собираю презентацию · слайд 6 · 14,2 КБ"
 *   "пишу deck.js · 14,2 КБ"
 *   "выполняю: Собрать XLSX"
 * Undefined when there is nothing better than the phase label itself.
 *
 * The size rides only with something it measures — a deck, a sheet, a file
 * being written. Alone, or behind a command's description, it is the length
 * of a tool call's JSON: «Шаг 6 / 6 · 2,1 КБ», «· 728 Б», a number ticking
 * four times a second that means nothing to the person reading it.
 */
export function describeToolActivity(activity: ToolActivity, t: Translate, locale: string) {
  // A file writer measures its file even before the path is known: `write`
  // declares `content` before `filePath`, and most models follow that order,
  // so the path arrives only after the whole body.
  const measured = !!activity.semantic || FILE_WRITERS.has(activity.tool)
  const size =
    measured && activity.state === "preparing" && activity.chars > 0
      ? formatStreamedSize(activity.chars, locale, {
          kb: t("ui.toolActivity.unit.kb"),
          b: t("ui.toolActivity.unit.b"),
        })
      : undefined
  const bits: string[] = []
  const semantic = activity.semantic
  if (semantic?.kind === "slides") bits.push(t("ui.toolActivity.slides", { count: semantic.count }))
  else if (semantic?.kind === "sheets")
    bits.push(
      semantic.rows > 0
        ? t("ui.toolActivity.sheetsRows", { count: semantic.count, rows: semantic.rows })
        : t("ui.toolActivity.sheets", { count: semantic.count }),
    )
  else if (semantic?.kind === "paragraphs") bits.push(t("ui.toolActivity.paragraphs", { count: semantic.count }))
  else if (activity.target)
    bits.push(
      activity.state === "running"
        ? t(RUNNING_KEYS[activity.tool] ?? "ui.toolActivity.runningTarget", { target: activity.target })
        : FILE_WRITERS.has(activity.tool)
          ? t("ui.toolActivity.writingFile", { target: activity.target })
          : t("ui.toolActivity.preparingTarget", { target: activity.target }),
    )
  else if (size && FILE_WRITERS.has(activity.tool)) bits.push(t("ui.toolActivity.writingUnnamed"))
  if (size) bits.push(size)
  return bits.length ? bits.join(" · ") : undefined
}
